import {X, Trash2, Edit2, ImageOff, CalendarDays, CreditCard, Palette} from 'lucide-react';
import {useNavigate} from 'react-router-dom';
import {useShopping} from '../contexts/ShoppingContext';
import {useGuestMode} from '../hooks/useGuestMode';
import {QuantitySelector, Modal} from './ui';
import {CalendarCustomizer} from './CalendarCustomizer';
import {BusinessCardCustomizer} from './BusinessCardCustomizer';
import {CustomizeItemCustomizer} from './CustomizeItemCustomizer';
import {CopyPersonalizationButton} from './CopyPersonalizationButton';
import {CalendarCustomization, BusinessCardCustomization, CartItem} from '../types';
import {getStableCartItemId} from '../utils/copyPersonalization';
import {useState, useEffect, useRef} from 'react';
import {gtm} from '../services/gtm';

const authCheckoutEnabled = import.meta.env.VITE_AUTH_CHECKOUT_ENABLED === 'true';

export function Cart() {
    const navigate = useNavigate();
    const {state, dispatch, cartInitialized} = useShopping();
    const {isGuest} = useGuestMode();
    const {cart, isCartOpen} = state;
    const [editingCartItem, setEditingCartItem] = useState<CartItem | null>(null);
    const [editingCustomization, setEditingCustomization] = useState<CalendarCustomization | null>(null);
    const [editingBusinessCardCustomization, setEditingBusinessCardCustomization] = useState<BusinessCardCustomization | null>(null);
    const [editingCustomizeItemCustomization, setEditingCustomizeItemCustomization] = useState<BusinessCardCustomization | null>(null);
    const [modalResetKey, setModalResetKey] = useState(0);

    const getDiscountTier = (item: CartItem) =>
        item.product.quantityDiscounts
            ?.filter(t => item.quantity >= t.minQuantity && (t.maxQuantity === null || item.quantity <= t.maxQuantity))
            .sort((a, b) => b.minQuantity - a.minQuantity)[0];

    const getDiscountedUnitPrice = (item: CartItem) => {
        const tier = getDiscountTier(item);
        return Number(tier ? tier.customPrice : item.variation.price);
    };

    const getPersonalizationFeePerUnit = (item: CartItem) =>
        item.personalizations?.reduce((sum, p) => sum + (p.fee ?? 0), 0) ?? 0;

    const getSecondLogoFeePerUnit = (item: CartItem) => item.secondLogoFee ?? 0;

    // Flat one-time charge per cart line item — NOT multiplied by quantity.
    const getCustomerChargeAmount = (item: CartItem) =>
        item.customerCharges?.reduce((sum, c) => sum + c.amount, 0) ?? 0;

    const originalTotal = cart.reduce((total, item) =>
        total + (Number(item.variation.price) + getPersonalizationFeePerUnit(item) + getSecondLogoFeePerUnit(item)) * item.quantity + getCustomerChargeAmount(item), 0);
    const totalPrice = cart.reduce((total, item) => {
        return total + (getDiscountedUnitPrice(item) + getPersonalizationFeePerUnit(item) + getSecondLogoFeePerUnit(item)) * item.quantity + getCustomerChargeAmount(item);
    }, 0);
    const totalSavings = originalTotal - totalPrice;

    useEffect(() => {
        if (isCartOpen) {
            gtm.pushViewCart(cart, totalPrice);
        }
    }, [isCartOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpdateQuantity = (cartItemId: string, newQuantity: number) => {
        dispatch({
            type: 'UPDATE_QUANTITY',
            payload: {cartItemId, quantity: newQuantity}
        });
    };

    const handleRemoveItem = (cartItemId: string) => {
        const item = cart.find(i => (i.id || `${i.productId}-${i.variationId}`) === cartItemId);
        if (item) gtm.pushRemoveFromCart(item);
        dispatch({
            type: 'REMOVE_FROM_CART',
            payload: cartItemId
        });
    };

    const handleClose = () => {
        dispatch({type: 'SET_CART_OPEN', payload: false});
    };

    const handleEditCustomization = (item: CartItem) => {
        setEditingCartItem(item);
        setEditingCustomization(item.calendarCustomization || null);
    };

    const handleEditBusinessCardCustomization = (item: CartItem) => {
        setEditingCartItem(item);
        setEditingBusinessCardCustomization(
            item.businessCardCustomization ?? (item.product.useCardBuilder ? {} as BusinessCardCustomization : null)
        );
    };

    const handleSaveCustomization = () => {
        if (editingCartItem && editingCustomization) {
            const cartItemId = editingCartItem.id || `${editingCartItem.productId}-${editingCartItem.variationId}`;
            dispatch({
                type: 'UPDATE_CALENDAR_CUSTOMIZATION',
                payload: {cartItemId, calendarCustomization: editingCustomization}
            });
            setEditingCartItem(null);
            setEditingCustomization(null);
        }
    };

    const handleSaveBusinessCardCustomization = () => {
        if (editingCartItem && editingBusinessCardCustomization) {
            const cartItemId = editingCartItem.id || `${editingCartItem.productId}-${editingCartItem.variationId}`;
            dispatch({
                type: 'UPDATE_BUSINESS_CARD_CUSTOMIZATION',
                payload: {cartItemId, businessCardCustomization: editingBusinessCardCustomization}
            });
            setEditingCartItem(null);
            setEditingBusinessCardCustomization(null);
        }
    };

    const handleEditCustomizeItemCustomization = (item: CartItem) => {
        setEditingCartItem(item);
        setEditingCustomizeItemCustomization(item.customizeItemCustomization || null);
    };

    const handleSaveCustomizeItemCustomization = () => {
        if (editingCartItem && editingCustomizeItemCustomization) {
            const cartItemId = editingCartItem.id || `${editingCartItem.productId}-${editingCartItem.variationId}`;
            dispatch({
                type: 'UPDATE_CUSTOMIZE_ITEM_CUSTOMIZATION',
                payload: {cartItemId, customizeItemCustomization: editingCustomizeItemCustomization}
            });
            setEditingCartItem(null);
            setEditingCustomizeItemCustomization(null);
        }
    };

    const handleCancelEdit = () => {
        setEditingCartItem(null);
        setEditingCustomization(null);
        setEditingBusinessCardCustomization(null);
        setEditingCustomizeItemCustomization(null);
    };

    // Mobile: Full-screen overlay - safely check window in browser environment
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

    const cartContent = (
        <>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <h2
                        className="text-xl font-semibold text-gray-900"
                        id="cart-title"
                    >
                        Shopping Cart
                    </h2>
                </div>

                <div className="flex items-center justify-between gap-4">
                    <button
                        onClick={() => {
                            handleClose();
                            navigate('/cart');
                        }}
                        className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold py-1.5 px-3 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        aria-label="View full shopping cart"
                    >
                        View Cart &rarr;
                    </button>

                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        aria-label="Close shopping cart"
                    >
                        <X className="w-5 h-5" aria-hidden="true"/>
                    </button>
                </div>
            </div>

            {!isGuest && !authCheckoutEnabled && (
                <div className="mx-6 mt-4 mb-2 flex flex-row p-4 bg-[#f0f7ff] border border-[#cfe4ff] rounded-lg font-sans">
                    <div className="mr-3 text-lg pt-0.5">
                        ✨
                    </div>
                    <div className="flex flex-col gap-2">
                        <h3 className="text-[#1a4a8e] text-base font-semibold leading-tight">
                            Checkout is coming soon!
                        </h3>
                        <p className="text-[#4a6fa5] text-sm leading-relaxed">
                            You're previewing our new checkout experience. Please use the current checkout to finish your order.
                        </p>
                        <button
                            onClick={() => {
                                handleClose();
                                window.location.href = '/cart';
                            }}
                            className="bg-[#1d5ed2] hover:bg-[#164eb3] text-white font-semibold py-2 px-4 rounded-md text-sm w-fit transition-colors duration-200"
                        >
                            Complete Your Order &rarr;
                        </button>
                    </div>
                </div>
            )}

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-6" role="region" aria-live="polite">
                {!cartInitialized ? (
                    <div className="space-y-4 animate-pulse" aria-busy="true" aria-label="Loading cart">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="flex gap-4 p-4 border border-gray-100 rounded-lg">
                                <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : cart.length === 0 ? (
                    <div className="text-center py-12" role="status">
                        <div
                            className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center"
                            aria-hidden="true">
                            <X className="w-8 h-8 text-gray-400"/>
                        </div>
                        <p className="text-gray-500 text-lg">Your cart is empty</p>
                        <p className="text-gray-800 text-sm mt-1">Add some items to get started</p>
                    </div>
                ) : (
                    <div className="space-y-6" role="list" aria-label="Cart items">
                        {Object.entries(
                            cart.reduce<Record<string, CartItem[]>>((groups, item) => {
                                const key = item.product.productionTimeline ?? 'Ships Soon';
                                (groups[key] ??= []).push(item);
                                return groups;
                            }, {})
                        ).map(([timelineName, items]) => (
                            <div key={timelineName}>
                                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                                    </svg>
                                    <span className="text-xs font-semibold text-gray-600">Shipping Timeline:</span>
                                    <span className="text-xs text-gray-900">{timelineName}</span>
                                </div>
                                <div className="space-y-3">
                                {items.map((item) => {
                                    const cartItemId = item.id || `${item.productId}-${item.variationId}`;
                                    const firstImage = item.variation.images && item.variation.images.length > 0
                                        ? item.variation.images[0]
                                        : item.product.images?.[0];
                                    const variationImage = firstImage == null ? null : typeof firstImage === 'string' ? firstImage : firstImage.url;
                                    return (
                                        <div key={cartItemId} className="p-3" role="listitem">
                                            <div className="flex gap-3">
                                            {/* Product Image */}
                                            <div className="flex-shrink-0">
                                                <button onClick={() => { handleClose(); navigate(`/product/${item.productId}`); }} className="block">
                                                    {variationImage ? (
                                                        <img
                                                            src={variationImage}
                                                            alt={`${item.product.name} - ${item.variation.color}${item.variation.size !== 'Standard' ? `, ${item.variation.size}` : ''}`}
                                                            className="w-16 h-16 rounded-lg object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                                                            <ImageOff className="w-6 h-6" />
                                                        </div>
                                                    )}
                                                </button>
                                            </div>

                                            {/* Product Details */}
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-sm font-semibold text-gray-900 truncate">
                                                    <button onClick={() => { handleClose(); navigate(`/product/${item.productId}`); }} className="hover:underline text-left">
                                                        {item.product.name}
                                                    </button>
                                                </h3>
                                                {item.product.promoCodeOnly && (
                                                    <div className="flex items-center gap-1 mt-0.5 text-blue-600 text-xs">
                                                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
                                                        </svg>
                                                        <span>Promo code only</span>
                                                    </div>
                                                )}
                                                <div className="mt-1 text-xs text-gray-500">
                                                    {[
                                                        item.variation.size !== 'Standard' && `Size ${item.variation.size}`,
                                                        item.variation.color,
                                                        item.logoOptionName && `Logo: ${item.logoOptionName}`,
                                                        item.secondLogoOptionName && `2nd Logo: ${item.secondLogoOptionName}`,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' • ')}
                                                </div>
                                                {item.customerCharges?.map((charge, i) => (
                                                    <p key={i} className="mt-1 text-xs text-gray-500">
                                                        {charge.name || 'One Time Charge'}: ${charge.amount.toFixed(2)}
                                                    </p>
                                                ))}
                                                {item.personalizations?.map((p, i) => (
                                                    <div key={i} className="mt-2 p-2 bg-blue-100 rounded-md border border-blue-200">
                                                        <p className="text-xs font-medium text-blue-900">
                                                            {item.personalizations!.length > 1 ? `Personalization #${i + 1}` : 'Personalization'}
                                                        </p>
                                                        <p className="text-xs text-blue-700">{p.text}</p>
                                                        {p.placement && (
                                                            <p className="text-xs text-blue-600">Placement: {p.placement}</p>
                                                        )}
                                                        {p.fee != null && p.fee > 0 && (
                                                            <p className="text-xs text-blue-600">+${p.fee.toFixed(2)} per unit</p>
                                                        )}
                                                    </div>
                                                ))}
                                                {item.calendarCustomization && (
                                                    <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-100">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-xs font-medium text-blue-900">Calendar Customization</p>
                                                            <button
                                                                onClick={() => handleEditCustomization(item)}
                                                                className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-white hover:bg-blue-50 border border-blue-200 rounded-md px-2 py-1 transition-colors focus:outline-none"
                                                                aria-label="Edit calendar customization"
                                                            >
                                                                <Edit2 className="w-3 h-3"/>
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {item.businessCardCustomization && (
                                                    <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-100">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-xs font-medium text-blue-900">Business Card Customization</p>
                                                            <button
                                                                onClick={() => handleEditBusinessCardCustomization(item)}
                                                                className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-white hover:bg-blue-50 border border-blue-200 rounded-md px-2 py-1 transition-colors focus:outline-none"
                                                                aria-label="Edit business card customization"
                                                            >
                                                                <Edit2 className="w-3 h-3"/>
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {item.customizeItemCustomization && (
                                                    <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-100">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-xs font-medium text-blue-900">Item Customization</p>
                                                            <button
                                                                onClick={() => handleEditCustomizeItemCustomization(item)}
                                                                className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-white hover:bg-blue-50 border border-blue-200 rounded-md px-2 py-1 transition-colors focus:outline-none"
                                                                aria-label="Edit item customization"
                                                            >
                                                                <Edit2 className="w-3 h-3"/>
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            </div>
                                            {(() => {
                                                const tier = getDiscountTier(item);
                                                const pFee = getPersonalizationFeePerUnit(item);
                                                const sloFee = getSecondLogoFeePerUnit(item);
                                                const unitPrice = getDiscountedUnitPrice(item) + pFee + sloFee;
                                                const originalUnitPrice = Number(item.variation.price) + pFee + sloFee;
                                                return (
                                                    <>
                                                        <hr className="mt-3 border-gray-200" />

                                                        <div className="pt-2 flex items-center justify-between">
                                                            <span className="text-xs text-gray-400">@</span>
                                                            {tier ? (
                                                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                                                    <span className="line-through">${originalUnitPrice.toFixed(2)}</span>
                                                                    <span>${unitPrice.toFixed(2)}</span>
                                                                    <span className="bg-green-100 text-green-700 rounded px-1">Qty Price</span>
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-gray-400">
                                                                    ${unitPrice.toFixed(2)} each
                                                                </span>
                                                            )}
                                                        </div>

                                                        <hr className="mt-2 border-gray-200" />

                                                        <div className="pt-2 flex items-center justify-between gap-2">
                                                            <QuantitySelector
                                                                quantity={item.quantity}
                                                                onQuantityChange={(newQuantity) => handleUpdateQuantity(cartItemId, newQuantity)}
                                                                size="sm"
                                                                stock={item.variation.stock}
                                                                allowOnDemand={item.product.allowOnDemand}
                                                                allowManualInput={true}
                                                            />
                                                            <div className="flex items-center gap-2">
                                                                <div className="flex flex-col items-end">
                                                                    <span className="text-xs text-gray-500">Subtotal</span>
                                                                    {tier && (
                                                                        <span className="text-xs text-gray-400 line-through">
                                                                            ${(originalUnitPrice * item.quantity + getCustomerChargeAmount(item)).toFixed(2)}
                                                                        </span>
                                                                    )}
                                                                    <span className="text-lg font-extrabold text-gray-900">
                                                                        ${(unitPrice * item.quantity + getCustomerChargeAmount(item)).toFixed(2)}
                                                                    </span>
                                                                </div>
                                                                <button
                                                                    onClick={() => handleRemoveItem(cartItemId)}
                                                                    className="p-1 hover:bg-red-100 hover:text-red-600 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                                                    aria-label={`Remove ${item.product.name} from cart`}
                                                                >
                                                                    <Trash2 className="w-4 h-4" aria-hidden="true"/>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    );
                                })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer */}
            {cart.length > 0 && (
                <div className="border-t border-gray-200 p-4" role="region" aria-label="Cart summary">
                    <div className="p-2">
                        <div className="space-y-2 mb-3 px-4">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Subtotal ({cart.length} {cart.length === 1 ? 'item' : 'items'})</span>
                                <span className="font-medium text-gray-900">${originalTotal.toFixed(2)}</span>
                            </div>
                            {totalSavings > 0.005 && (
                                <div className="flex justify-between text-sm text-green-600">
                                    <span>Quantity Discount</span>
                                    <span>-${totalSavings.toFixed(2)}</span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col px-4">
                            <span className="text-sm font-medium text-gray-500 capitalize tracking-wide">Order Total</span>
                            <span className="text-2xl font-bold text-green-600">${totalPrice.toFixed(2)}</span>
                        </div>
                        <hr className="border-gray-200 my-4" />
                        <div className="px-4">
                            <button
                                onClick={() => {
                                    gtm.pushBeginCheckout(cart, totalPrice);
                                    handleClose();
                                    navigate('/checkout');
                                }}
                                disabled={!isGuest && !authCheckoutEnabled}
                                className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-base py-3.5 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                aria-label={`Proceed to checkout with total $${totalPrice.toFixed(2)}`}
                            >
                                Proceed to Checkout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    // Mobile: Full screen
    if (isMobile) {
        return (
            <>
                <div
                    className={`fixed inset-0 z-50 bg-white transition-transform duration-300 ease-in-out ${
                        isCartOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="cart-title"
                >
                    <div className="h-full flex flex-col">
                        {cartContent}
                    </div>
                </div>

                {/* Edit Calendar Customization Modal */}
                {editingCartItem && editingCustomization && (
                    <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Calendar Customization">
                        <div className="flex justify-end mb-4">
                            <CopyPersonalizationButton
                                targetProductType="calendar"
                                targetProduct={editingCartItem.product}
                                currentDestValue={editingCustomization ?? undefined}
                                excludeCartItemId={getStableCartItemId(editingCartItem)}
                                onApply={(merged) => {
                                    setEditingCustomization(merged as CalendarCustomization);
                                    setModalResetKey(k => k + 1);
                                }}
                            />
                        </div>
                        <div className="max-h-[70vh] overflow-y-auto">
                            <CalendarCustomizer
                                key={modalResetKey}
                                onCustomizationChange={setEditingCustomization}
                                initialCustomization={editingCustomization ?? undefined}
                                previewImageUrl={editingCartItem.product.printImages?.frontForReview}
                                style={editingCartItem.product.printOnDemandStyle}
                                fields={editingCartItem.product.printOnDemandFields}
                            />
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveCustomization}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Save Changes
                            </button>
                        </div>
                    </Modal>
                )}

                {/* Edit Business Card Customization Modal */}
                {editingCartItem && editingBusinessCardCustomization !== null && (
                    <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Business Card Customization" size={"lg"}>
                        <div className="flex justify-end mb-4">
                            <CopyPersonalizationButton
                                targetProductType="business_card"
                                targetProduct={editingCartItem.product}
                                currentDestValue={editingBusinessCardCustomization ?? undefined}
                                excludeCartItemId={getStableCartItemId(editingCartItem)}
                                onApply={(merged) => {
                                    setEditingBusinessCardCustomization(merged as BusinessCardCustomization);
                                    setModalResetKey(k => k + 1);
                                }}
                            />
                        </div>
                        <BusinessCardCustomizer
                            key={modalResetKey}
                            onCustomizationChange={setEditingBusinessCardCustomization}
                            withBorder={false}
                            initialCustomization={editingBusinessCardCustomization}
                            productImages={editingCartItem.product.images}
                            previewImage={editingCartItem.product.printImages?.frontForReview}
                            style={editingCartItem.product.printOnDemandStyle}
                            fields={editingCartItem.product.printOnDemandFields}
                            options={editingCartItem.product.printOnDemandOptions}
                            useCardBuilder={editingCartItem.product.useCardBuilder}
                            cardTemplateId={editingCartItem.product.cardTemplateId}
                        />
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveBusinessCardCustomization}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Save Changes
                            </button>
                        </div>
                    </Modal>
                )}

                {/* Edit Customize Item Modal */}
                {editingCartItem && editingCustomizeItemCustomization && (
                    <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Item Customization" size={"lg"}>
                        <div className="flex justify-end mb-4">
                            <CopyPersonalizationButton
                                targetProductType="customize_item"
                                targetProduct={editingCartItem.product}
                                currentDestValue={editingCustomizeItemCustomization ?? undefined}
                                excludeCartItemId={getStableCartItemId(editingCartItem)}
                                onApply={(merged) => {
                                    setEditingCustomizeItemCustomization(merged as BusinessCardCustomization);
                                    setModalResetKey(k => k + 1);
                                }}
                            />
                        </div>
                        <CustomizeItemCustomizer
                            key={modalResetKey}
                            onCustomizationChange={setEditingCustomizeItemCustomization}
                            withBorder={false}
                            initialCustomization={editingCustomizeItemCustomization}
                            productImages={editingCartItem.product.images}
                            previewImage={editingCartItem.product.printImages?.frontForReview}
                            fields={editingCartItem.product.printOnDemandFields}
                            options={editingCartItem.product.printOnDemandOptions}
                        />
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveCustomizeItemCustomization}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Save Changes
                            </button>
                        </div>
                    </Modal>
                )}
            </>
        );
    }

    // Desktop: Side panel
    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 z-50 bg-black/25 backdrop-blur-sm transition-opacity duration-300 ${
                    isCartOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                }`}
                onClick={handleClose}
                aria-hidden="true"
            />

            {/* Side Panel */}
            <div
                className={`fixed z-[100] right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
                    isCartOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="cart-title"
            >
                <div className="h-full flex flex-col">
                    {cartContent}
                </div>
            </div>

            {/* Edit Calendar Customization Modal */}
            {editingCartItem && editingCustomization && (
                <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Calendar Customization" size={"lg"}>
                    <div className="flex justify-end mb-4">
                        <CopyPersonalizationButton
                            targetProductType="calendar"
                            targetProduct={editingCartItem.product}
                            currentDestValue={editingCustomization ?? undefined}
                            excludeCartItemId={getStableCartItemId(editingCartItem)}
                            onApply={(merged) => {
                                setEditingCustomization(merged as CalendarCustomization);
                                setModalResetKey(k => k + 1);
                            }}
                        />
                    </div>
                    <CalendarCustomizer
                            key={modalResetKey}
                            onCustomizationChange={setEditingCustomization}
                            initialCustomization={editingCustomization}
                            previewImageUrl={editingCartItem.product.printImages?.frontForReview}
                            style={editingCartItem.product.printOnDemandStyle}
                            fields={editingCartItem.product.printOnDemandFields}
                            withBorder={false}
                        />
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={handleCancelEdit}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveCustomization}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Save Changes
                        </button>
                    </div>
                </Modal>
            )}

            {/* Edit Business Card Customization Modal */}
            {editingCartItem && editingBusinessCardCustomization !== null && (
                <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Business Card Customization" size={"lg"}>
                    <div className="flex justify-end mb-4">
                        <CopyPersonalizationButton
                            targetProductType="business_card"
                            targetProduct={editingCartItem.product}
                            currentDestValue={editingBusinessCardCustomization ?? undefined}
                            excludeCartItemId={getStableCartItemId(editingCartItem)}
                            onApply={(merged) => {
                                setEditingBusinessCardCustomization(merged as BusinessCardCustomization);
                                setModalResetKey(k => k + 1);
                            }}
                        />
                    </div>
                     <BusinessCardCustomizer
                            key={modalResetKey}
                            onCustomizationChange={setEditingBusinessCardCustomization}
                            withBorder={false}
                            initialCustomization={editingBusinessCardCustomization}
                            productImages={editingCartItem.product.images}
                            previewImage={editingCartItem.product.printImages?.frontForReview}
                            style={editingCartItem.product.printOnDemandStyle}
                            fields={editingCartItem.product.printOnDemandFields}
                            options={editingCartItem.product.printOnDemandOptions}
                            useCardBuilder={editingCartItem.product.useCardBuilder}
                            cardTemplateId={editingCartItem.product.cardTemplateId}
                        />
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={handleCancelEdit}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveBusinessCardCustomization}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Save Changes
                        </button>
                    </div>
                </Modal>
            )}

            {/* Edit Customize Item Modal */}
            {editingCartItem && editingCustomizeItemCustomization && (
                <Modal isOpen={true} onClose={handleCancelEdit} title="Edit Item Customization" size={"lg"}>
                    <div className="flex justify-end mb-4">
                        <CopyPersonalizationButton
                            targetProductType="customize_item"
                            targetProduct={editingCartItem.product}
                            currentDestValue={editingCustomizeItemCustomization ?? undefined}
                            excludeCartItemId={getStableCartItemId(editingCartItem)}
                            onApply={(merged) => {
                                setEditingCustomizeItemCustomization(merged as BusinessCardCustomization);
                                setModalResetKey(k => k + 1);
                            }}
                        />
                    </div>
                    <CustomizeItemCustomizer
                        key={modalResetKey}
                        onCustomizationChange={setEditingCustomizeItemCustomization}
                        withBorder={false}
                        initialCustomization={editingCustomizeItemCustomization}
                        productImages={editingCartItem.product.images}
                        previewImage={editingCartItem.product.printImages?.frontForReview}
                        fields={editingCartItem.product.printOnDemandFields}
                        options={editingCartItem.product.printOnDemandOptions}
                    />
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={handleCancelEdit}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveCustomizeItemCustomization}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Save Changes
                        </button>
                    </div>
                </Modal>
            )}
        </>
    );
}
